import { FormControlLabel, formControlLabelClasses, styled } from '@mui/material';
import { useId } from 'react';

import { Radio } from '../../atoms/Radio';

type Props = {
  value: string;
  label: string;
  /** What choosing this option means, shown under the label and announced with the radio. */
  description?: string;
  disabled?: boolean;
  'data-testid'?: string;
};

const OptionLabel = styled(FormControlLabel)(({ theme }) => ({
  alignItems: 'flex-start',
  marginInline: 0,
  paddingBlock: theme.spacing(0.5),
  gap: theme.spacing(0.5),
  [`&.${formControlLabelClasses.disabled}`]: {
    cursor: 'default',
  },
}));

const Text = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  // Aligns the label's first line with the 42px radio target.
  paddingTop: theme.spacing(1.25),
  minWidth: 0,
}));

const Label = styled('span')(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.primary,
}));

const Description = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

/**
 * One choice of a `RadioGroup` with a short explanation under its label, for choices whose
 * consequence must be read before picking (e.g. a member's role). The whole option is clickable.
 */
export function RadioOption(props: Props) {
  const labelId = useId();
  const descriptionId = useId();
  // The label element wraps the description too: name the radio after its label only.
  const inputProps = {
    'aria-labelledby': labelId,
    'aria-describedby': props.description ? descriptionId : undefined,
  };

  return (
    <OptionLabel
      value={props.value}
      disabled={props.disabled}
      data-testid={props['data-testid'] ?? 'radio-option'}
      disableTypography
      control={(
        <Radio slotProps={{ input: inputProps }} />
      )}
      label={(
        <Text>
          <Label id={labelId}>{props.label}</Label>
          {props.description ? <Description id={descriptionId}>{props.description}</Description> : null}
        </Text>
      )}
    />
  );
}

export type { Props as RadioOptionProps };
